/* The material, edited where it lives — one pattern for every level.
 *
 * The tree on the left only selects. Whatever is selected, the middle belongs
 * to it: a form over its own data on top, and its children as a table below —
 * a school lists its subjects, a subject its lessons, a lesson its goal levels
 * and its kit, a kit item the blocks of its finished material. Clicking a row
 * opens the child over the whole middle; the breadcrumb is the way back up.
 * The right column carries the state and the history of the selected level and
 * nothing else.
 *
 * Founding happens where the thing will live and with all its fields: a school
 * is the taxonomy's strand (name, short name, school type, years, who it is
 * for), a subject its subject (name, area), a lesson a draft document with its
 * whole head. There is no second, thinner way to create any of them.
 *
 * Saving is immediate and field by field: a curated input saves the one value
 * it holds when the author leaves it, through the same PATCH the layered model
 * serves. The JSON mode stays as a view and an emergency input, saved whole.
 * The safety net is the history beside it, at the level the edit happened.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type ContentEdit, type ContentRow, type ContentVersion,
  type Mark, type Me, type Translatable, type TranslateRun,
  type Voiceable, type VoiceRun } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import type { Taxonomy } from "../../lib/types";
import { EdMain } from "./ContentMain";
import { EdSide } from "./ContentSide";
import { EdTree } from "./ContentTree";

export const PUBLISHED = "published";
export const DRAFT = "draft";

/** Where a block sits inside its document: ["blocks",3] or
 *  ["kit",2,"ready","blocks",0] or ["parts",1,"blocks",4]. */
export type BlockPath = (string | number)[];

/** What is selected. The tree fills the top half; the tables in the middle
 *  fill `sub`, which the tree cannot reach — a kit item has no branch. */
export interface Sel {
  school?: string;
  subject?: string;
  pages?: boolean;
  apps?: boolean;
  taxJson?: boolean;
  doc?: string;
  sub?: { kind: "kit"; i: number } | { kind: "goal"; i: number }
      | { kind: "part"; i: number } | { kind: "block"; path: BlockPath };
  creating?: "school" | "subject" | "lesson" | "page";
}

export interface Run {
  kind: string; code: string; note: string; lines: number; done: boolean; err?: string;
}

/** Everything the three columns share. One object, so the views stay functions. */
export interface Ed {
  me: Me;
  rows: ContentRow[];
  tax: Taxonomy | null;
  sel: Sel;
  go: (sel: Sel) => void;
  /* the loaded document — buffers as in the JSON mode, parsed for the forms */
  text: string; saved: string; parsed: Record<string, unknown> | null;
  /** Bumped when the document is replaced under the forms (load, revert, whole
   *  save) — the uncontrolled inputs re-key on it and pick the new values up. */
  ver: number;
  setText: (t: string) => void;
  dirty: boolean;
  problems: string[]; warnings: string[]; msg: string; busy: boolean;
  jsonMode: boolean; setJsonMode: (on: boolean) => void;
  saveField: (path: BlockPath, value?: unknown) => void;
  saveWhole: (status?: string) => Promise<void>;
  saveTax: (mutate: (t: Taxonomy) => Taxonomy) => Promise<void>;
  removeDoc: () => Promise<void>;
  create: (what: NonNullable<Sel["creating"]>, f: Record<string, string>) => Promise<void>;
  versions: ContentVersion[]; edits: ContentEdit[];
  /** English halves a machine wrote and nobody has read — by address. */
  marks: Mark[];
  translate: () => Promise<void>;
  confirmMarks: () => Promise<void>;
  /** Read one material's script aloud and point the item at the recording. */
  voice: (kind: string) => Promise<void>;
  revertVersion: (id: string) => Promise<void>;
  revertEdit: (id: string) => Promise<void>;
  run: Run | null; setRun: (r: Run | null) => void;
  fill: (kind: string) => Promise<void>;
  keepApp: () => Promise<void>;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The subtree of a document at a path, or undefined. */
export function getAt(root: unknown, path: BlockPath): unknown {
  let at: unknown = root;
  for (const step of path) {
    if (at === null || typeof at !== "object") return undefined;
    at = (at as Record<string | number, unknown>)[step];
  }
  return at;
}

/** The path as the server's edit route names it: kit[2].title.cs */
export const asPath = (path: BlockPath): string =>
  path.map((p) => (typeof p === "number" ? `[${p}]` : `.${p}`)).join("").replace(/^\./, "");

export function Content({ me }: { me: Me }) {
  const { ui } = useI18n();

  const [rows, setRows] = useState<ContentRow[] | null>(null);
  const [tax, setTax] = useState<Taxonomy | null>(null);
  const [sel, setSel] = useState<Sel>({});
  const [text, setText] = useState("");
  const [saved, setSaved] = useState("");
  const [ver, setVer] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [edits, setEdits] = useState<ContentEdit[]>([]);
  const [run, setRun] = useState<Run | null>(null);
  const [marks, setMarks] = useState<Mark[]>([]);

  const list = useCallback(async () => {
    setRows(await api<ContentRow[]>("/admin/content").catch(() => []));
  }, []);

  /* Straight from the API, not through lib/content.ts: in development that
     loader reads the git seed from disk, and an editor that writes the
     database while reading the disk would show yesterday's taxonomy. */
  const loadTax = useCallback(async () => {
    const res = await fetch("/api/content/taxonomy.json", { credentials: "same-origin" });
    setTax(res.ok ? await res.json().catch(() => null) : null);
  }, []);

  useEffect(() => { void list(); void loadTax(); }, [list, loadTax]);

  const loadDoc = useCallback(async (path: string) => {
    const res = await fetch(`/api/content/${path}`, { credentials: "same-origin" });
    const body = res.ok ? await res.text() : "";
    setText(body); setSaved(body); setVer((v) => v + 1);
    if (path.startsWith("docs/")) {
      const q = encodeURIComponent(path);
      const [whole, fine, machine] = await Promise.all([
        api<ContentVersion[]>(`/admin/content/history?path=${q}`).catch(() => []),
        api<ContentEdit[]>(`/admin/content/edits?path=${q}`).catch(() => []),
        api<Mark[]>(`/admin/content/marks?path=${q}`).catch(() => []),
      ]);
      setVersions(whole); setEdits(fine); setMarks(machine);
    } else {
      const q = encodeURIComponent(path);
      setEdits([]);
      setVersions(await api<ContentVersion[]>(`/admin/content/history?path=${q}`).catch(() => []));
    }
  }, []);

  const go = useCallback((next: Sel) => {
    setProblems([]); setMsg(""); setJsonMode(false); setRun(null);
    if (next.doc !== sel.doc) {
      setWarnings([]);
      setText(""); setSaved(""); setVersions([]); setEdits([]); setMarks([]);
      if (next.doc) void loadDoc(next.doc);
    }
    setSel(next);
  }, [loadDoc, sel.doc]);

  const parsed = useMemo(() => {
    if (!sel.doc?.endsWith(".json")) return null;
    try {
      const v = JSON.parse(text) as unknown;
      return isObject(v) ? v : null;
    } catch { return null; }
  }, [text, sel.doc]);

  const dirty = text !== saved;

  /* ── one field, saved as the author leaves it ─────────────────────────── */

  /* Saves run one after another whatever order their blurs fired in, and the
     ref mirrors `saved` because a queued save runs after later renders. */
  const queue = useRef(Promise.resolve());
  const savedRef = useRef("");
  useEffect(() => { savedRef.current = saved; }, [saved]);

  const saveField = useCallback((fieldPath: BlockPath, value?: unknown) => {
    const at = sel.doc;
    if (!at) return;
    queue.current = queue.current.then(async () => {
      let last: unknown;
      try { last = JSON.parse(savedRef.current); } catch { return; }
      const before = getAt(last, fieldPath);
      const removing = value === undefined;
      if (removing && before === undefined) return;
      if (!removing && JSON.stringify(before) === JSON.stringify(value)) return;

      setProblems([]); setMsg("");
      /* The curated inputs are uncontrolled, so a clean buffer stays equal to
         `saved` while the author types — remembered here, before this save
         moves it, to tell a clean buffer from a JSON-mode draft. */
      const cleanBefore = savedRef.current;
      try {
        const out = await api<{ text: string; warnings?: string[] }>(`/admin/content/${at}`, {
          method: "PATCH",
          body: JSON.stringify(removing
            ? { at: asPath(fieldPath) }
            : { at: asPath(fieldPath), value }),
        });
        setSaved(out.text);
        setText((cur) => {
          /* Adopt the server's canonical text whenever the buffer holds no
             other unsaved work: either it was clean, or it already says the
             same thing. A JSON-mode draft in progress is the author's and is
             left alone; it saves whole on its own button. */
          if (cur === cleanBefore) return out.text;
          try {
            if (JSON.stringify(JSON.parse(cur)) === JSON.stringify(JSON.parse(out.text))) {
              return out.text;
            }
          } catch { /* an unparsable buffer is the author's, leave it */ }
          return cur;
        });
        setWarnings(out.warnings ?? []);
        setMsg(ui("content.saved"));
        const q = encodeURIComponent(at);
        setEdits(await api<ContentEdit[]>(`/admin/content/edits?path=${q}`).catch(() => []));
      } catch (err) {
        const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
        setProblems((err as { problems?: string[] })?.problems ?? [detail]);
      }
    });
  }, [sel.doc, ui]);

  /* ── the whole document: the JSON mode, publishing ────────────────────── */
  const saveWhole = useCallback(async (status?: string) => {
    if (!sel.doc) return;
    setBusy(true); setProblems([]); setMsg("");
    try {
      const out = await api<{ warnings?: string[] }>(`/admin/content/${sel.doc}`, {
        method: "PUT",
        body: JSON.stringify({ data: text, ...(status ? { status } : {}) }),
      });
      setWarnings(out.warnings ?? []);
      setMsg(ui("content.saved"));
      await loadDoc(sel.doc);
      await list();
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
      setProblems((err as { problems?: string[] })?.problems ?? [detail]);
    } finally {
      setBusy(false);
    }
  }, [list, loadDoc, sel.doc, text, ui]);

  /* ── the taxonomy: schools and subjects live there ────────────────────── */
  const saveTax = useCallback(async (mutate: (t: Taxonomy) => Taxonomy) => {
    if (!tax) return;
    setProblems([]); setMsg("");
    try {
      const next = mutate(JSON.parse(JSON.stringify(tax)) as Taxonomy);
      await api(`/admin/content/taxonomy.json`, {
        method: "PUT",
        body: JSON.stringify({ data: JSON.stringify(next, null, 2) + "\n" }),
      });
      setTax(next);
      setMsg(ui("content.saved"));
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    }
  }, [tax, ui]);

  const removeDoc = useCallback(async () => {
    if (!sel.doc || !confirm(ui("content.deleteAsk"))) return;
    setBusy(true);
    try {
      await api(`/admin/content/${sel.doc}`, { method: "DELETE" });
      await list();
      go({ ...sel, doc: undefined, sub: undefined });
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    } finally {
      setBusy(false);
    }
  }, [go, list, sel, ui]);

  /* ── founding, with all the fields of the level ───────────────────────── */
  const create = useCallback(async (what: NonNullable<Sel["creating"]>,
                                    f: Record<string, string>) => {
    setProblems([]);
    const id = (f["id"] ?? "").trim();
    if (!/^[a-z][a-z0-9-]{0,31}$/.test(id)) {
      setProblems([ui("content.idHint")]);
      return;
    }
    try {
      if (what === "school") {
        const from = Number(f["yearFrom"]) || 1, to = Number(f["yearTo"]) || from;
        const years = Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i);
        await saveTax((t) => {
          t.strandIds = [...t.strandIds, id];
          t.strands[id] = {
            id, schoolType: f["schoolType"] === "ss" ? "ss" : "zs",
            years,
            name: { cs: f["name"] ?? "", ...(f["nameEn"] ? { en: f["nameEn"] } : {}) },
            short: { cs: f["short"] || (f["name"] ?? ""), ...(f["shortEn"] ? { en: f["shortEn"] } : {}) },
            who: { cs: f["who"] ?? "" },
          };
          return t;
        });
        setMsg(ui("content.schoolMade"));
        go({ school: id });
      } else if (what === "subject") {
        await saveTax((t) => {
          t.subjects[id] = { id, name: { cs: f["name"] ?? "", ...(f["nameEn"] ? { en: f["nameEn"] } : {}) },
            area: f["area"] ?? Object.keys(tax?.areas ?? {})[0] ?? "" };
          return t;
        });
        setMsg(ui("content.subjectMade"));
        go({ school: sel.school, subject: id });
      } else if (what === "lesson") {
        const strand = sel.school!;
        const year = Number(f["year"]) || 1;
        const doc = {
          id, type: "lesson",
          meta: {
            subject: sel.subject, strand: [strand], year,
            grade: f["grade"] || `${year}. ročník`,
            schoolType: tax?.strands[strand]?.schoolType ?? "zs",
          },
          /* Bare string when there is no English: the canonical form writes a
             cs-only value as the shorthand, and a { cs } object would not
             survive the model's round trip. */
          title: f["titleEn"] ? { cs: f["title"] ?? "", en: f["titleEn"] } : f["title"] ?? "",
          ...(f["claim"] ? { claim: f["claim"] } : {}),
          goal: { levels: ["zaklad", "bezny", "rozsireny"].map((lv) =>
            ({ id: lv, who: "", can: "", proof: "" })) },
          kit: [],
          blocks: [],
        };
        const out = await api<{ warnings?: string[] }>(`/admin/content/docs/${id}.json`, {
          method: "PUT",
          body: JSON.stringify({ data: JSON.stringify(doc, null, 2), status: DRAFT }),
        });
        await list();
        go({ school: sel.school, subject: sel.subject, doc: `docs/${id}.json` });
        setWarnings(out.warnings ?? []);
        setMsg(ui("content.lessonMade"));
      } else if (what === "page") {
        const doc = { id, type: "page", title: f["title"] ?? "", blocks: [] };
        await api(`/admin/content/docs/${id}.json`, {
          method: "PUT",
          body: JSON.stringify({ data: JSON.stringify(doc, null, 2), status: DRAFT }),
        });
        await list();
        go({ pages: true, doc: `docs/${id}.json` });
        setMsg(ui("content.pageMade"));
      }
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
      setProblems((err as { problems?: string[] })?.problems ?? [detail]);
    }
  }, [go, list, saveTax, sel, tax, ui]);

  /* ── history, both memories ───────────────────────────────────────────── */
  const afterRevert = useCallback(async () => {
    if (!sel.doc) return;
    await loadDoc(sel.doc);
    setMsg(ui("content.reverted"));
    await list();
  }, [list, loadDoc, sel.doc, ui]);

  const revertVersion = useCallback(async (id: string) => {
    if (!sel.doc) return;
    setBusy(true);
    try {
      await api("/admin/content/revert", { method: "POST",
        body: JSON.stringify({ path: sel.doc, version: id }) });
      await afterRevert();
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    } finally { setBusy(false); }
  }, [afterRevert, sel.doc, ui]);

  const revertEdit = useCallback(async (id: string) => {
    if (!sel.doc) return;
    setBusy(true);
    try {
      await api("/admin/content/edits/revert", { method: "POST",
        body: JSON.stringify({ path: sel.doc, id }) });
      await afterRevert();
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    } finally { setBusy(false); }
  }, [afterRevert, sel.doc, ui]);

  /* ── the translator ───────────────────────────────────────────────────── */

  /* The estimate is asked for first and the author answers the question with
     the numbers in it: how many fields, how many characters, what it costs.
     A button that spends money without saying how much is a button people stop
     pressing after the first surprise. */
  const translate = useCallback(async () => {
    if (!sel.doc) return;
    setProblems([]); setMsg("");
    let ask: Translatable;
    try {
      ask = await api<Translatable>(
        `/admin/content/translatable?path=${encodeURIComponent(sel.doc)}`);
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
      return;
    }
    if (!ask.ready) { setProblems([ui("content.noTranslator")]); return; }
    if (!ask.fields) { setMsg(ui("content.allTranslated")); return; }
    const price = ask.costUsd < 0.01 ? "<0,01" : ask.costUsd.toFixed(2);
    if (!confirm(`${ui("content.translateAsk")}\n\n`
      + `${ask.fields} ${ui("content.fieldsUnit")} · `
      + `${ask.chars.toLocaleString("cs")} ${ui("content.charsUnit")} · ~$${price}\n`
      + `${ui("content.spentToday")}: ${ask.spentToday.toLocaleString("cs")} / `
      + `${ask.dailyChars.toLocaleString("cs")}`)) return;

    setBusy(true);
    try {
      const out = await api<TranslateRun>("/admin/content/translate", {
        method: "POST", body: JSON.stringify({ path: sel.doc }),
      });
      await loadDoc(sel.doc);
      setMsg(`${ui("content.translated")} ${out.fields} · `
        + `${out.chars.toLocaleString("cs")} ${ui("content.charsUnit")}`);
      // Not an error and not a silence: these lines came back with the markdown
      // broken, so the Czech stayed and a person has to do them by hand.
      if (out.refused.length) {
        setWarnings(out.refused.map((at) => `${ui("content.refusedField")} ${at}`));
      }
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
      setProblems((err as { problems?: string[] })?.problems ?? [detail]);
    } finally {
      setBusy(false);
    }
  }, [loadDoc, sel.doc, ui]);

  /* Read, and right as it came. Editing the text confirms it by itself — this
     is the button for the far more common case, where it needed no editing. */
  const confirmMarks = useCallback(async () => {
    if (!sel.doc) return;
    setBusy(true);
    try {
      await api("/admin/content/confirm", {
        method: "POST", body: JSON.stringify({ path: sel.doc }),
      });
      setMarks([]);
      setMsg(ui("content.confirmed"));
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    } finally {
      setBusy(false);
    }
  }, [sel.doc, ui]);

  /* ── the voice ────────────────────────────────────────────────────────── */

  /* The same shape as the translation, for the same reason: the author is
     asked with the numbers in the question, and a recording that already
     exists says so, because re-reading a corrected script replaces it. */
  const voice = useCallback(async (kind: string) => {
    if (!sel.doc) return;
    setProblems([]); setMsg("");
    let ask: Voiceable;
    try {
      ask = await api<Voiceable>(`/admin/content/voiceable`
        + `?path=${encodeURIComponent(sel.doc)}&kind=${encodeURIComponent(kind)}`);
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
      setProblems([detail]);
      return;
    }
    if (!ask.ready) { setProblems([ui("content.noVoice")]); return; }
    const price = ask.costUsd < 0.01 ? "<0,01" : ask.costUsd.toFixed(2);
    if (!confirm(`${ui(ask.recorded ? "content.voiceAgainAsk" : "content.voiceAsk")}\n\n`
      + `${ask.chars.toLocaleString("cs")} ${ui("content.charsUnit")} · ~$${price} · `
      + `${ask.voice} / ${ask.model}\n`
      + `${ui("content.spentToday")}: ${ask.spentToday.toLocaleString("cs")} / `
      + `${ask.dailyChars.toLocaleString("cs")}`)) return;

    setBusy(true);
    try {
      const out = await api<VoiceRun>("/admin/content/voice", {
        method: "POST", body: JSON.stringify({ path: sel.doc, kind }),
      });
      await loadDoc(sel.doc);
      setMsg(`${ui("content.voiced")} ${Math.round(out.bytes / 1024)} kB · `
        + `${out.seconds} s · ${out.chars.toLocaleString("cs")} ${ui("content.charsUnit")}`);
    } catch (err) {
      const detail = err instanceof ApiError ? err.message : ui("admin.saveFailed");
      setProblems((err as { problems?: string[] })?.problems ?? [detail]);
    } finally {
      setBusy(false);
    }
  }, [loadDoc, sel.doc, ui]);

  /* ── the generator, run from the item it fills ────────────────────────── */
  const fill = useCallback(async (kind: string) => {
    if (!sel.doc) return;
    setRun({ kind, code: "", note: "", lines: 0, done: false });
    let res: Response;
    try {
      res = await fetch("/api/admin/content/generate", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: sel.doc, kind }),
      });
    } catch {
      setRun((r) => r && { ...r, done: true, err: ui("admin.saveFailed") });
      return;
    }
    if (!res.ok || !res.body) {
      const detail = await res.json().catch(() => ({}));
      setRun((r) => r && { ...r, done: true, err: detail.detail ?? ui("admin.saveFailed") });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let rest = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      rest += decoder.decode(value, { stream: true });
      const lines = rest.split("\n");
      rest = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        let obj: Record<string, unknown>;
        try { obj = JSON.parse(line); } catch { continue; }
        setRun((r) => {
          if (!r) return r;
          if (typeof obj["err"] === "string") return { ...r, err: obj["err"] };
          return { ...r,
            code: r.code + (typeof obj["c"] === "string" ? obj["c"] : ""),
            note: r.note + (typeof obj["n"] === "string" ? obj["n"] : ""),
            lines: r.lines + 1 };
        });
      }
    }
    setRun((r) => r && { ...r, done: true });
  }, [sel.doc, ui]);

  /* The answer itself lands as a draft application the author has just read;
     what the lesson gets is only the `ready` pointer, through the same edit
     door as any field — so the history remembers what the item carried. */
  const keepApp = useCallback(async () => {
    if (!run || !parsed || !run.code.trim() || sel.sub?.kind !== "kit") return;
    const name = `${String(parsed["id"] ?? "")}-${run.kind}`;
    setBusy(true);
    try {
      await api(`/admin/content/apps/${name}.html`, {
        method: "PUT", body: JSON.stringify({ data: run.code, status: DRAFT }),
      });
      saveField(["kit", sel.sub.i, "ready"], { as: "app", app: name });
      setRun(null);
      await list();
      setMsg(ui("content.kept"));
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : ui("admin.saveFailed")]);
    } finally {
      setBusy(false);
    }
  }, [list, parsed, run, saveField, sel.sub, ui]);

  if (rows === null) return null;

  const ed: Ed = {
    me, rows, tax, sel, go,
    text, saved, parsed, ver, setText, dirty,
    problems, warnings, msg, busy,
    jsonMode, setJsonMode,
    saveField, saveWhole, saveTax, removeDoc, create,
    versions, edits, marks, revertVersion, revertEdit, translate, confirmMarks, voice,
    run, setRun, fill, keepApp,
  };

  return (
    <section className="adm-panel edx">
      <EdTree ed={ed} />
      <EdMain ed={ed} />
      <EdSide ed={ed} />
    </section>
  );
}
